import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  AddressChangeRequest,
  AERequestStatusType,
  ChangeRequestData,
  ChangeRequestHistoryEntry,
  DirectDepositRequest,
  RequestData,
  RequestType
} from '../models/change-requests';
import { of } from 'rxjs';
import { EmployeeDirectDepositExtended } from '../models/employee-direct-deposit';

@Injectable({
  providedIn: 'root'
})
export class ChangeRequestService {
  constructor(
    private http: HttpClient,
    @Inject('env') private env: any
  ) {}

  public canEnableChangeRequests(clientCode: string) {
    return this.http.get<{ Value: boolean }>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/ChangeRequests/Enable`
    );
  }

  public submitAddressChangeRequest(
    clientCode: string,
    personId: number,
    request: AddressChangeRequest
  ) {
    return this.http.post(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/Address`,
      request
    );
  }
  public submitDirectDepositChangeRequest(
    clientCode: string,
    personId: number,
    deposits: EmployeeDirectDepositExtended[]
  ) {
    return this.http.post(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/DirectDeposit`,
      deposits
    );
  }
  public hasAddressChangeRequest(
    clientCode: string,
    personId: number,
    isRequester: boolean = true
  ) {
    let params = new HttpParams();
    if (isRequester) {
      params = params.set('userType', 1);
    } else {
      params = params.set('userType', 2);
    }
    return this.http.get<{ Value: boolean }>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/Address/HasOpenRequest`,
      { params }
    );
  }
  public hasDirectDepositRequest(
    clientCode: string,
    personId: number,
    isRequester: boolean = true
  ) {
    let params = new HttpParams();
    if (isRequester) {
      params = params.set('userType', 1);
    } else {
      params = params.set('userType', 2);
    }
    return this.http.get<{ Value: boolean }>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/DirectDeposit/HasOpenRequest`,
      { params }
    );
  }
  public cancelOpenDirectDepositRequest(clientCode: string, personId: number) {
    return this.http.delete(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/DirectDeposit/Open`
    );
  }
  public cancelOpenAddressRequest(clientCode: string, personId: number) {
    return this.http.delete(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/Address/Open`
    );
  }

  public getAddressChangeData(clientCode: string, personId: number, isRequester: boolean = true) {
    let params = new HttpParams();
    if (isRequester) {
      params = params.set('userType', 1);
    } else {
      params = params.set('userType', 2);
    }
    return this.http.get<ChangeRequestData<AddressChangeRequest>>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/Address/Open/Data`,
      { params }
    );
  }
  public getOpenAddressRequest(clientCode: string, personId: number, isRequester: boolean = true) {
    let params = new HttpParams();
    if (isRequester) {
      params = params.set('userType', 1);
    } else {
      params = params.set('userType', 2);
    }
    return this.http.get<ChangeRequestHistoryEntry>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/Address/Open`,
      { params }
    );
  }

  public getOpenDirectDepositRequest(
    clientCode: string,
    personId: number,
    isRequester: boolean = true
  ) {
    let params = new HttpParams();
    if (isRequester) {
      params = params.set('userType', 1);
    } else {
      params = params.set('userType', 2);
    }
    return this.http.get<ChangeRequestHistoryEntry>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/DirectDeposit/Open`,
      { params }
    );
  }
  public getDirectDepositChangeData(
    clientCode: string,
    personId: number,
    isRequester: boolean = true
  ) {
    let params = new HttpParams();
    if (isRequester) {
      params = params.set('userType', 1);
    } else {
      params = params.set('userType', 2);
    }
    return this.http.get<ChangeRequestData<DirectDepositRequest>>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/DirectDeposit/Open/Data`,
      { params }
    );
  }
  public getFinalizedAddressRequestData(clientCode: string, personId: number, requestId: number) {
    return this.http.get<ChangeRequestData<AddressChangeRequest>>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/Address/${requestId}/Data`
    );
  }

  public getFinalizedDirectDepositRequestData(
    clientCode: string,
    personId: number,
    requestId: number
  ) {
    return this.http.get<ChangeRequestData<DirectDepositRequest>>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests/DirectDeposit/${requestId}/Data`
    );
  }
  public getRequestsApprover(clientCode: string, status: AERequestStatusType = null) {
    let params = new HttpParams();
    params = params.set('status', status);
    return this.http.get<ChangeRequestHistoryEntry[]>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/ChangeRequests`,
      { params }
    );
  }

  public getRequestsRequester(
    clientCode: string,
    personId: number,
    status: AERequestStatusType = null
  ) {
    let params = new HttpParams();
    if (status != null) {
      params = params.set('status', status);
    }
    return this.http.get<ChangeRequestHistoryEntry[]>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/People/${personId}/ChangeRequests`,
      { params }
    );
  }

  public getAddressRequestData(clientCode: string, requestId: number) {
    return this.http.get<ChangeRequestData<AddressChangeRequest>>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/ChangeRequests/Address/${requestId}/Data`
    );
  }

  public getDirectDepositRequestData(clientCode: string, requestId: number) {
    return this.http.get<ChangeRequestData<DirectDepositRequest>>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/ChangeRequests/DirectDeposit/${requestId}/Data`
    );
  }

  public approveAddressRequest(clientCode: string, requestId: number, comment: string) {
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/ChangeRequests/Address/${requestId}/Accept`,
      {
        Comment: comment
      }
    );
  }

  public approveDirectDepositRequest(clientCode: string, requestId: number, comment: string) {
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/ChangeRequests/DirectDeposit/${requestId}/Accept`,
      {
        Comment: comment
      }
    );
  }

  public declineRequest(clientCode: string, requestId: number, comment: string, type: RequestType) {
    return this.http.put(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/ChangeRequests/${requestId}/Decline`,
      {
        Comment: comment,
        Type: type
      }
    );
  }
}
